import { useContext, useState } from "react";
import { Alert, Button, Modal } from "react-bootstrap";
import ShareholderForm from "./create_edit_shareholder";
import ShareholdersTable from "./shareholders_list";
import {
  deleteShareholderRequest,
} from "@/services";
import { formatApiErrorMessage } from "@/services/_apiConfig";

import "../kyc.css";
import { useParams } from "react-router-dom";
import { custom_context } from "@/utils/custom_context";
import { totalHolding } from "./shareholding_rules";

const ShareholdingPattern = ({ goToNextStep, shareholders, reloadShareholders, shareholdingCoverage }) => {
  const coverageErrors = shareholdingCoverage?.coverage_errors || [];
  const uboThreshold = shareholdingCoverage?.ubo_shareholding_threshold;
  const { applicationId } = useParams();
  const { applicationData } = useContext(custom_context.ApplicationData);
  const isCreditOfferApproved = applicationData?.evaluation?.status === "approved";
  const [editingId, setEditingId] = useState(null);
  // Business shareholder the new row belongs to, null for the borrower company.
  const [addingUnder, setAddingUnder] = useState(null);
  const [showForm, setShowForm] = useState(
    shareholders?.length === 0 && !isCreditOfferApproved
  );
  const [loading, setLoading] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState("");

  const handleAdd = (company) => {
    setEditingId(null);
    setAddingUnder(company);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    if (isCreditOfferApproved || item.is_kyc_verified) return;
    setEditingId(item.uuid);
    setAddingUnder(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setAddingUnder(null);
  };

  const handleRemove = (item) => {
    if (isCreditOfferApproved || item.is_kyc_verified) return;
    setRemoveError("");
    setRemoveTarget(item);
  };

  const closeRemoveModal = () => {
    if (removing) return;
    setRemoveTarget(null);
    setRemoveError("");
  };

  // Remove shareholder after confirmation
  const confirmRemove = async () => {
    if (!removeTarget?.uuid || removing) return;
    setRemoving(true);
    setRemoveError("");
    try {
      const res = await deleteShareholderRequest(removeTarget.uuid, { loan_id: applicationId });
      if (res?.success === false) {
        setRemoveError(formatApiErrorMessage(res.error, "Failed to remove this shareholder. Please try again."));
        // The row may have been verified since this list was loaded, so pull the
        // current state in rather than leaving a stale row that looks removable.
        await reloadShareholders();
        return;
      }
      await reloadShareholders();
      setRemoveTarget(null);
    } catch (error) {
      console.error("Error deleting shareholder:", error);
      setRemoveError("Failed to remove this shareholder. Please try again.");
    } finally {
      setRemoving(false);
    }
  };

  const editingShareholder =
    editingId !== null ? shareholders.find((s) => s.uuid === editingId) : null;

  // The 100% cap applies within one company, so only count rows from the same pool.
  const poolUuid = editingShareholder
    ? editingShareholder.business_shareholder || null
    : addingUnder?.uuid || null;
  const parentCompany = poolUuid
    ? (shareholders || []).find((s) => s.uuid === poolUuid)
    : null;
  const otherShareholdingTotal = totalHolding(
    (shareholders || []).filter(
      (s) =>
        (s.business_shareholder || null) === poolUuid &&
        s.uuid !== editingShareholder?.uuid
    )
  );

  return (
    <div className="_innerKyc_grid ing_exMarg" style={{ width: '100%' }}>
      <div className="_inKycHead">
        <h1>Shareholding Pattern</h1>
        <p>
          Please add details of key shareholders.
          {uboThreshold != null &&
            ` Mandatory to add all shareholders holding more than ${uboThreshold}% shareholding.`}{" "}
          For a business shareholder, also add the individuals who own it.
        </p>
      </div>

      {isCreditOfferApproved && (
        <div className="alert alert-info" role="alert">
          Shareholders cannot be added, edited or removed after the credit offer has been approved.
        </div>
      )}

      {loading ? (
        <div className="loader-container">
          <p>Fetching shareholders...</p>
        </div>
      ) : showForm ? (
        <ShareholderForm
          reloadShareholders={reloadShareholders}
          isCreditOfferApproved={isCreditOfferApproved}
          initialData={editingShareholder}
          parentCompany={parentCompany}
          uboThreshold={uboThreshold}
          otherShareholdingTotal={otherShareholdingTotal}
          onCancel={closeForm}
        />
      ) : (
        <ShareholdersTable
          shareholders={shareholders}
          coverageErrors={coverageErrors}
          isCreditOfferApproved={isCreditOfferApproved}
          onEdit={handleEdit}
          onRemove={handleRemove}
          onAdd={handleAdd}
          goToNextStep={goToNextStep}
        />
      )}

      <Modal show={!!removeTarget} onHide={closeRemoveModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Remove Shareholder</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to remove <b>{removeTarget?.name || "this shareholder"}</b>?
          {removeError && (
            <Alert variant="danger" className="mt-3 mb-0">{removeError}</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeRemoveModal} disabled={removing}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmRemove} disabled={removing}>
            {removing ? "Removing..." : "Remove"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ShareholdingPattern;
